/**
 * นำทาง (shared systems/nav.ts) in the HUD: a "นำทาง" button on every line of the quest tracker (`#qp`), the places
 * and people named in the tracker and in a quest giver's words (`#dlgText`) as links. A button pressed again stops the walk.
 *
 * The HUD rebuilds the tracker's lines (hud.ts `renderQuest`); a watcher on `#qp` and `#dlgText` dresses them again
 * each time, so nothing in hud.ts changes. A walk that leaves the zone by a warp toward its goal is sent again in the
 * next zone (after the reload), from sessionStorage.
 */
import { autoQuestOf, navLinks, navOf, type NavTarget, type PlayerCommand, type World } from '@nine/shared';
import { h } from './dom';

const RESUME = 'nine.nav.resume';
/** Tries at sending the walk again after a warp (the world may not be started yet), and the time between (s). */
const RESUME_TRIES = 8;
const RESUME_GAP = 1.5;

const CSS = `
#qp .nav-go{margin-left:6px;padding:0 6px;font:inherit;font-size:11px;line-height:16px;border-radius:3px;
  border:1px solid rgba(236,190,84,.75);background:rgba(40,20,10,.55);color:#ffe6a0;cursor:pointer;pointer-events:auto}
#qp .nav-go:hover{background:rgba(120,60,20,.7)}
#qp .nav-go.on{background:#ecbe54;color:#2a1408}
.nav-link{color:#ffe6a0;text-decoration:underline dotted;text-underline-offset:2px;cursor:pointer;pointer-events:auto}
.nav-link:hover{color:#fff3c8}
`;

const keyOf = (t: NavTarget): string => JSON.stringify(t);

export function installQuestNav(world: World, send: (c: PlayerCommand) => void): void {
  // Online (a replica of the server's World) there is no navigation system here: nothing to show.
  if (!navOf(world)) return;
  if (!document.getElementById('quest-nav-css'))
    document.head.append(h('style', { id: 'quest-nav-css' }, CSS));
  const state = () => navOf(world)?.state ?? null;
  const go = (target: NavTarget) => send({ t: 'navTo', target });
  /** Starts a walk, or stops the one under way to the same target. */
  const toggle = (target: NavTarget): void => {
    const st = state();
    if (st && keyOf(st.target) === keyOf(target)) send({ t: 'navStop' });
    else go(target);
  };

  /** A control that acts on press without taking the focus (Space must stay the jump). */
  const press = (el: HTMLElement, run: () => void) => {
    el.addEventListener('pointerdown', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      run();
    });
    el.addEventListener('click', (ev) => ev.stopPropagation());
  };

  /** A line's text with the places it names made links. */
  const linkify = (el: HTMLElement, after?: () => void) => {
    if (el.querySelector('.nav-link')) return;
    const parts = navLinks(el.textContent ?? '');
    if (!parts.some((p) => p.place)) return;
    el.replaceChildren(
      ...parts.map((p) => {
        if (!p.place) return document.createTextNode(p.text);
        const a = h('span', { class: 'nav-link', title: `นำทางไป${p.text}` }, p.text);
        const place = p.place;
        press(a, () => {
          go({ kind: 'place', place });
          after?.();
        });
        return a;
      }),
    );
  };

  /** What a tracker line leads to: a kill objective its beasts, anything else the quest's next step. */
  const lineTarget = (k: number): NavTarget => {
    const q = world.currentQuest();
    const o = world.progress.quest.status === 'active' ? q?.objectives[k] : undefined;
    return o?.type === 'kill' && o.target ? { kind: 'monster', monster: o.target } : { kind: 'quest' };
  };

  const nextBtns: HTMLButtonElement[] = [];
  const buttons: { el: HTMLButtonElement; key: string }[] = [];
  /** The walk the buttons show ('' for none). */
  let shown = '';
  const qp = document.getElementById('qp');
  const dress = () => {
    if (!qp) return;
    buttons.length = 0;
    nextBtns.length = 0;
    // No quest: the line is the free-roaming note, with nothing to walk to.
    if (!world.currentQuest()) return;
    [...qp.children].forEach((line, k) => {
      if (!(line instanceof HTMLElement) || line.querySelector('.nav-go')) return;
      linkify(line);
      const target = lineTarget(k);
      const el = h('button', { type: 'button', class: 'nav-go', tabIndex: -1 }, 'นำทาง') as HTMLButtonElement;
      press(el, () => toggle(target));
      line.append(el);
      if (k === 0) {
        // J8: "ต่อไป" walks and talks through the quest steps (shared systems/autoQuest.ts); fights stay manual.
        const nx = h('button', { type: 'button', class: 'nav-go', tabIndex: -1 }, 'ต่อไป') as HTMLButtonElement;
        press(nx, () => send({ t: 'autoQuest', on: !autoQuestOf(world)?.on }));
        line.append(nx);
        nextBtns.push(nx);
      }
      buttons.push({ el, key: keyOf(target) });
    });
    shown = '';
  };
  if (qp) new MutationObserver(dress).observe(qp, { childList: true });
  dress();
  const dlg = document.getElementById('dlgText');
  if (dlg)
    new MutationObserver(() =>
      linkify(dlg, () => (document.getElementById('dlgClose') as HTMLButtonElement | null)?.click()),
    ).observe(dlg, { childList: true });

  // The buttons show the walk under way ("หยุด" on the one it is for); a walk off through a warp is kept for the next zone.
  let last: NavTarget | null = null;
  const zone = world.map.zone;
  let resume = readResume();
  let tries = 0;
  let nextTry = 0;
  const tick = () => {
    const aq = !!autoQuestOf(world)?.on;
    for (const b of nextBtns) {
      b.classList.toggle('on', aq);
      b.textContent = aq ? 'หยุด' : 'ต่อไป';
    }
    const st = state();
    const key = st ? keyOf(st.target) : '';
    if (key !== shown) {
      shown = key;
      for (const b of buttons) {
        const on = b.key === key;
        b.el.classList.toggle('on', on);
        b.el.textContent = on ? 'หยุด' : 'นำทาง';
      }
    }
    if (last && !st && world.progress.location !== zone && last.kind !== 'npc' && last.kind !== 'point')
      writeResume(last);
    last = st?.target ?? null;
    // After a warp: send the walk again until it runs, once the world has started (the splash is gone).
    const started = document.getElementById('splash')?.hidden !== false;
    if (resume && !st && started && world.time >= nextTry) {
      if (tries++ >= RESUME_TRIES) resume = null;
      else {
        nextTry = world.time + RESUME_GAP;
        if (!world.scene) go(resume);
      }
    } else if (resume && st) resume = null;
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

function readResume(): NavTarget | null {
  try {
    const raw = sessionStorage.getItem(RESUME);
    sessionStorage.removeItem(RESUME);
    return raw ? (JSON.parse(raw) as NavTarget) : null;
  } catch {
    return null;
  }
}

function writeResume(t: NavTarget): void {
  try {
    sessionStorage.setItem(RESUME, JSON.stringify(t));
  } catch {
    // Private mode: the walk is not carried on.
  }
}
